import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useOrganisationDetail } from "../hooks/useOrganisations";
import { useRoles } from "../hooks/useRoles";
import { useInviteUserToOrg, useRemoveUserFromOrg } from "../hooks/useOrgMembers";
import { useDeleteOrganisation } from "../hooks/useDeleteOrganisation";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RoleSelector } from "../components/ui/RoleSelector";
import type { BadgeVariant } from "../components/ui/Badge";
import type { OrgUser, Role } from "../domain/types/admin.types";
import { HttpError } from "../services/http";

function statusVariant(status: string): BadgeVariant {
  switch (status.toUpperCase()) {
    case "ACTIVE": return "success";
    case "PENDING_ACTIVATION": return "warning";
    case "DEACTIVATED": return "danger";
    default: return "default";
  }
}

function statusLabel(status: string): string {
  switch (status.toUpperCase()) {
    case "ACTIVE": return "Active";
    case "PENDING_ACTIVATION": return "Pending";
    case "DEACTIVATED": return "Deactivated";
    default: return status;
  }
}

const inviteSchema = z.object({
  email: z.string().email("Invalid email address"),
});
type InviteForm = z.infer<typeof inviteSchema>;

const SYSTEM_ROLES = new Set(["ROLE_ADMIN", "ROLE_USER"]);

export const OrganisationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgId = id ?? "";

  const { data: org, isLoading, isError, error } = useOrganisationDetail(orgId);
  const rolesQuery = useRoles();
  const allRoles: Role[] = rolesQuery.data ?? [];
  const assignableRoles = allRoles.filter((r) => !SYSTEM_ROLES.has(r.name));

  const inviteMutation = useInviteUserToOrg(orgId);
  const removeMutation = useRemoveUserFromOrg(orgId);
  const deleteMutation = useDeleteOrganisation();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [inviteError, setInviteError] = useState("");

  const [removeTarget, setRemoveTarget] = useState<OrgUser | null>(null);
  const [removeError, setRemoveError] = useState("");

  const { register, handleSubmit, reset, formState: { errors, isValid } } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    mode: "onChange",
  });

  const handleInviteOpen = () => {
    reset();
    setSelectedRoles([]);
    setInviteError("");
    inviteMutation.reset();
    setInviteOpen(true);
  };

  const handleInviteClose = () => {
    setInviteOpen(false);
    setInviteError("");
  };

  const handleInviteSubmit = async (data: InviteForm) => {
    setInviteError("");
    try {
      await inviteMutation.mutateAsync({ email: data.email, roles: selectedRoles });
      handleInviteClose();
    } catch (err) {
      setInviteError(err instanceof HttpError ? err.message : "Failed to invite user.");
    }
  };

  const isLastUser = (org?.users.length ?? 0) === 1;

  const handleRemoveClick = (user: OrgUser) => {
    setRemoveError("");
    setRemoveTarget(user);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget || !org) return;
    setRemoveError("");
    try {
      await removeMutation.mutateAsync(removeTarget.id);
      if (isLastUser) {
        await deleteMutation.mutateAsync(orgId);
        navigate("/organisations");
        return;
      }
      setRemoveTarget(null);
    } catch (err) {
      setRemoveError(err instanceof HttpError ? err.message : "Failed to remove user.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-12">
        <Spinner /> Loading…
      </div>
    );
  }

  if (isError || !org) {
    return (
      <div className="py-12 text-sm text-red-500">
        {(error as any)?.data?.message ?? "Failed to load organisation."}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Back */}
      <button
        onClick={() => navigate("/organisations")}
        className="text-xs text-link-blue hover:underline self-start"
      >
        ← Back to Organisations
      </button>

      {/* Org Header */}
      <div className="bg-white rounded border border-border-gray px-6 py-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{org.name}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Created{" "}
            {new Date(org.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Users */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Users ({org.users.length})
          </h3>
          <Button onClick={handleInviteOpen}>+ Invite User</Button>
        </div>

        {org.users.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400 bg-white rounded border border-border-gray">
            No users in this organisation.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-border-gray bg-white">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray bg-default-99">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Roles</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-gray">
                {org.users.map((user: OrgUser) => (
                  <tr key={user.id} className="hover:bg-default-99 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{user.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariant(user.status)}>
                        {statusLabel(user.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.length === 0 ? (
                          <span className="text-gray-400 text-xs">—</span>
                        ) : (
                          [...user.roles].sort().map((role) => (
                            <Badge key={role} variant="default">
                              {role.replace("ROLE_", "")}
                            </Badge>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRemoveClick(user)}
                        className="text-xs text-red-500 hover:underline px-2 py-1"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <Modal
        open={inviteOpen}
        onClose={handleInviteClose}
        title="Invite User"
        className="max-w-md"
      >
        <form onSubmit={handleSubmit(handleInviteSubmit)} className="px-6 py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Email</label>
            <Input
              type="email"
              placeholder="user@example.com"
              {...register("email")}
              error={!!errors.email}
              className="w-full"
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          {assignableRoles.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Roles</label>
              <RoleSelector
                roles={assignableRoles}
                selected={selectedRoles}
                onChange={setSelectedRoles}
                maxHeight="max-h-52"
              />
            </div>
          )}

          {inviteError && <p className="text-xs text-red-500">{inviteError}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleInviteClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || inviteMutation.isPending}>
              {inviteMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4 border-white border-r-transparent" />
                  Sending…
                </span>
              ) : (
                "Send Invitation"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Remove Confirmation Modal */}
      {removeTarget && (
        <Modal
          open={!!removeTarget}
          onClose={() => { setRemoveTarget(null); setRemoveError(""); }}
          title={isLastUser ? "Remove Last User" : "Remove User"}
          className="max-w-sm"
        >
          <div className="px-6 py-4 flex flex-col gap-4">
            {isLastUser ? (
              <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p className="font-semibold mb-1">Warning: this will delete the organisation</p>
                <p>
                  <strong>{removeTarget.email}</strong> is the last member of{" "}
                  <strong>{org.name}</strong>. Removing them will also permanently delete this
                  organisation and all its data.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                Remove <strong>{removeTarget.email}</strong> from <strong>{org.name}</strong>?
                They will be deactivated and lose access immediately.
              </p>
            )}

            {removeError && <p className="text-xs text-red-500">{removeError}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { setRemoveTarget(null); setRemoveError(""); }}
                disabled={removeMutation.isPending || deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleRemoveConfirm}
                disabled={removeMutation.isPending || deleteMutation.isPending}
              >
                {removeMutation.isPending || deleteMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <Spinner className="h-4 w-4" />
                    Removing…
                  </span>
                ) : isLastUser ? (
                  "Remove & Delete Organisation"
                ) : (
                  "Remove User"
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
